<template>
  <CollectionLayout
    ref="collectionLayout"
    :onDeselect="onDeselect"
    :isLoading="isLoading">
    <CMToolbar
      slot="toolbar"
      :nav="toolbarNavItem"
      :title="title">
      <MUIMenu :items="titleMenuItems" anchor="right" v-if="titleMenuItems.length > 0">
        <CMButton icon="expand_more" name="Edit Collection" slot="button" />
      </MUIMenu>
      <CMButton v-bind="searchActionItem()" />
      <MUIMenu :items="toolbarActionItems" anchor="right" v-if="toolbarActionItems.length > 0">
        <CMButton icon="add_box" name="More" slot="button" />
      </MUIMenu>
    </CMToolbar>
    <RecipeListItem
      v-for="recipe in recipes"
      ref="listItems"
      :key="recipe.key"
      :recipe="recipe"
      :isSelected="recipe.key === selectedRecipeKey"
      @click.native="showDetail(recipe.key)" />
    <RecipeCollectionZeroState
      v-if="isDefined && isEmpty && !isLoading"
      v-bind="emptyState" />
  </CollectionLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import CollectionLayout from '@/layouts/CollectionLayout'

import CMButton from '@/components/CMButton'
import CMToolbar from '@/components/CMToolbar'
import MUIMenu from '@/components/MUIMenu'
import RecipeCollectionZeroState from '@/components/Recipe/CollectionZeroState'
import RecipeListItem from '@/components/Recipe/ListItem'

import {
  SET_DRAWER_VISIBILITY,
  SET_TEMPORARY_VIEW_VISIBILITY,
  TOGGLE_FULLSCREEN,
  REGISTER_SHORTCUT,
  DEREGISTER_SHORTCUT
} from '@/modules/mutation-types'

export default {
  name: 'RecipeCollection',
  components: {
    CMButton,
    RecipeCollectionZeroState,
    RecipeListItem,
    CMToolbar,
    MUIMenu,
    CollectionLayout
  },
  props: {
    collectionKey: String,
    hidePrimaryHandler: Function,
    onDeselect: Function,
    onSelect: Function,
    selectedRecipeKey: String
  },
  data () {
    return {}
  },
  computed: {
    ...mapGetters([
      'getCollectionById'
    ]),
    classNames () {
      return {
        'is-empty': Object.keys(this.recipes).length === 0
      }
    },
    collection () {
      return this.getCollectionById(this.collectionKey)
    },
    emptyState () {
      if (!this.collection) return
      if (this.collection.notice) return this.collection.notice
      return {
        title: 'No Recipes',
        message: `You haven't added any recipes to your "${this.collection.name}"\u00a0collection.`
      }
    },
    isEmpty () {
      return Object.keys(this.recipes).length === 0
    },
    isDefined () {
      return !!this.recipes
    },
    isLoading () {
      return this.$store.state.Recipe.loading
    },
    isSmartCollection () {
      return this.collection && this.collection.smart
    },
    isUserCollection () {
      return this.collection && this.collection.type === 'user'
    },
    recipes () {
      let getters = this.$store.getters
      switch (this.collectionKey) {
        case 'library':
          return getters.allRecipes()
        case 'uncategorized':
          return getters.uncategorizedRecipes()
        case 'recent':
          return getters.recentlyAddedRecipes()
        default:
          return getters.collectionRecipes(this.collectionKey)
      }
    },
    recipeIndex () {
      return this.recipeKeys.indexOf(this.selectedRecipeKey)
    },
    recipeKeys () {
      return this.recipes.map(e => { return e.key })
    },
    // TODO: Move to CollectionLayout.vue
    // atStartOfList () {
    //   return this.recipeIndex === 0
    // },
    // atEndOfList () {
    //   return this.recipeIndex === this.recipes.length - 1
    // },
    titleMenuItems () {
      if (!this.isUserCollection) return []

      return [{
        name: 'Edit Collection',
        fn: () => {
          this.$store.commit(SET_TEMPORARY_VIEW_VISIBILITY, {
            name: 'CollectionEditDialog',
            props: {
              collectionKey: this.collectionKey
            }
          })
        }
      }, {
        name: 'Delete Collection',
        fn: () => {
          this.$store.commit(SET_TEMPORARY_VIEW_VISIBILITY, {
            name: 'ConfirmDialog',
            props: {
              title: 'Delete collection?',
              message: `Deleting this collection won't affect your saved recipes.`,
              acceptItem: {
                name: 'Delete',
                fn: this.deleteHandler
              },
              dismissItem: {
                name: 'Cancel'
              }
            }
          })
        }
      }]
    },
    toolbarActionItems () {
      if (this.isSmartCollection || !this.collection) return []
      return [{
        name: 'Add by URL',
        icon: 'link',
        classNames: {
          'cm-button--transparent': true
        },
        fn: this.showAddByUrlModal
      }, {
        name: 'Add Manually',
        icon: 'add_box',
        classNames: {
          'cm-button--transparent': true
        },
        fn: this.showAddManuallyModal
      }]
    },
    toolbarNavItem () {
      let visibility = this.$store.state.app.drawer.visibility === 'visible'
        ? 'hidden'
        : 'visible'
      return {
        icon: 'menu',
        fn: () => { this.$store.commit(SET_DRAWER_VISIBILITY, { visibility }) }
      }
    },
    title () {
      if (!this.collection) return
      return this.collection.title || this.collection.name
    }
  },
  methods: {
    deleteHandler () {
      this.$router.currentRoute.path !== '/' && this.$router.push('/')
      let collection = this.collection
      this.$store.dispatch('destroyCollection', {
        uid: this.$store.state.User.uid,
        collectionKey: this.collectionKey
      })
      this.$track('collection.delete', {
        context: 'collection',
        ability: 'delete',
        collectionId: collection.key,
        collectionName: collection.name
      })
      return true
    },
    // TODO: Move to CollectionLayout
    // nextRecipe () {
    //   if (this.atEndOfList) return
    //   this.showDetail(this.recipeKeys[this.recipeIndex + 1])
    // },
    // previousRecipe () {
    //   if (this.atStartOfList) return
    //   this.showDetail(this.recipeKeys[this.recipeIndex - 1])
    // },
    // firstRecipe () {
    //   this.showDetail(this.recipeKeys[0])
    // },
    // lastRecipe () {
    //   this.showDetail(this.recipeKeys[this.recipes.length - 1])
    // },
    resetScroll () {
      let collectionLayout = this.$refs.collectionLayout
      if (collectionLayout) collectionLayout.resetScroll()
    },
    searchActionItem () {
      return {
        name: 'Search',
        icon: 'search',
        classNames: {
          'cm-button--transparent': true
        },
        fn: () => this.$router.currentRoute.path !== '/search' && this.$router.push('/search')
      }
    },
    // TODO: Move to CollectionLayout
    // scrollIntoView () {
    //   this.$nextTick(() => {
    //     var selectedItem = this.$refs.listItems.find(listItem => {
    //       return listItem.isSelected
    //     }).$el
    //     var itemRect = selectedItem.getBoundingClientRect()
    //     var scrollLayout = this.$refs.collectionLayout.$refs.scrollLayout.$el
    //     var layoutRect = scrollLayout.getBoundingClientRect()
    //     if (itemRect.top >= (layoutRect.bottom - itemRect.height)) {
    //       selectedItem.scrollIntoView(false)
    //     } else if (itemRect.bottom <= (layoutRect.top + itemRect.height)) {
    //       selectedItem.scrollIntoView(true)
    //     }
    //   })
    // },
    showAddByUrlModal () {
      this.$store.commit(SET_TEMPORARY_VIEW_VISIBILITY, {
        name: 'AddDialog',
        props: {
          collectionKey: this.collectionKey
        }
      })
    },
    showAddManuallyModal () {
      this.$store.commit(SET_TEMPORARY_VIEW_VISIBILITY, {
        name: 'RecipeNewDialog',
        props: {
          collectionKey: this.collectionKey
        }
      })
    },
    showDetail (recipeKey) {
      const path = this.$urlHelpers.collectionRecipePath(
        this.collection.key,
        recipeKey
      )
      this.$router.currentRoute.path !== path && this.$router.push(path)
      this.onSelect(recipeKey)
    },
    trackView () {
      this.$track('Collection Viewed', {
        context: 'collection',
        ability: 'view',
        id: this.collectionKey
      })
    },
    updateTitle () {
      const parts = ['Cookmark']
      if (this.collection && this.collection.key !== 'library') {
        parts.unshift(`${this.collection.name} Recipes`)
      }
      document.title = parts.join(' - ')
    }
  },
  watch: {
    collectionKey: {
      immediate: true,
      handler () {
        this.resetScroll()
        if (this.$store.state.app.view.fullscreen) this.$store.commit(TOGGLE_FULLSCREEN)
        this.trackView()
        this.updateTitle()
      }
    }
  },
  mounted () {
    // this.$store.commit(REGISTER_SHORTCUT, {
    //   name: 'add-recipe',
    //   keycode: 65,
    //   fn: this.showAddByUrlModal,
    //   scope: 'application'
    // })
    this.$store.commit(REGISTER_SHORTCUT, {
      name: 'search-collection',
      keycode: 191,
      fn: () => this.$router.currentRoute.path !== '/search' && this.$router.push('/search'),
      scope: 'application'
    })
    // TODO: Move to CollectionLayout
    // this.$store.commit(REGISTER_SHORTCUT, {
    //   name: 'previous-item',
    //   keycode: 38,
    //   fn: this.previousRecipe,
    //   scope: 'application'
    // })
    // this.$store.commit(REGISTER_SHORTCUT, {
    //   name: 'first-item',
    //   keycode: 38,
    //   altKey: true,
    //   fn: this.firstRecipe,
    //   scope: 'application'
    // })
    // this.$store.commit(REGISTER_SHORTCUT, {
    //   name: 'next-item',
    //   keycode: 40,
    //   fn: this.nextRecipe,
    //   scope: 'application'
    // })
    // this.$store.commit(REGISTER_SHORTCUT, {
    //   name: 'last-item',
    //   keycode: 40,
    //   altKey: true,
    //   fn: this.lastRecipe,
    //   scope: 'application'
    // })
  },
  beforeDestroy () {
    // this.$store.commit(DEREGISTER_SHORTCUT, 'add-recipe')
    this.$store.commit(DEREGISTER_SHORTCUT, 'search-collection')
  }
}
</script>



// WEBPACK FOOTER //
// src/components/Recipe/Collection.vue